/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { InteractiveComponents } from '../../../core/enums/InteractiveComponents';
import { Log } from '../../../core/logging';
import { setActiveInteractiveComponent } from '../../interactivelayer/interactiveLayer.reducers';
import {
    loadGridGuideChannels,
    loadGridGuideNextChannels,
    loadGridGuidePreviousChannels,
    onLeftSetPreviousPage,
    onRightSetNextPage,
    guideSetCaretInvisible,
    guideSetCaretVisible,
    turnOffPastGuideMode,
    turnOnPastGuideMode,
} from '../guide.reducers';
import {
    setVerticalAndHorizontalPositionAction,
    setVerticalPositionAction,
} from '../guidehighlight/guidehighlight.actions';
import {
    disableGhostMode,
    enableGhostMode,
    setHorizontalSession,
} from '../guidehighlight/guidehighlight.reducers';
import {
    startGuidePageScrollAction,
    stopGuidePageScrollAction,
} from '../turboscroll/turboscroll.actions';
import { onKeyOkGuideOperations, onLongKeyOkGuideOperations } from './guidekeyoperations.reducers';

const TAG: string = 'GuideMouseOperations';

export const onMouseClickGuideOperations = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;

    const position = calculateHighlightPosition(value);
    if (!position) return;

    dispatch(onKeyOkGuideOperations());
};

export const onMouseClickGuideInteractiveLayerOperations =
    () => (dispatch: Function, getState: Function) => {
        const { activeComponent } = getState().interactiveLayer;
        switch (activeComponent) {
            case InteractiveComponents.EAST:
                dispatch(onMouseClickGuideEastInteractiveComponent());
                break;
            case InteractiveComponents.NORTH:
                dispatch(loadGridGuidePreviousChannels());
                break;
            case InteractiveComponents.SOUTH:
                dispatch(loadGridGuideNextChannels());
                break;
            case InteractiveComponents.WEST:
                dispatch(onMouseClickGuideWestInteractiveComponent());
                break;
            default:
        }
    };

export const onMouseOkLongPressStartGuideOperations = () => (dispatch: Function) => {
    dispatch(onLongKeyOkGuideOperations());
};

export const onMouseOkLongPressStartInteractiveLayerOperations =
    () => (dispatch: Function, getState: Function) => {
        const { activeComponent } = getState().interactiveLayer;
        switch (activeComponent) {
            case InteractiveComponents.NORTH:
                dispatch(startGuidePageTurboScroll(true));
                break;
            case InteractiveComponents.SOUTH:
                dispatch(startGuidePageTurboScroll());
                break;
            default:
        }
    };

export const onMouseOkLongPressStopInteractiveLayerOperations =
    () => (dispatch: Function, getState: Function) => {
        const { activeComponent } = getState().interactiveLayer;
        switch (activeComponent) {
            case InteractiveComponents.NORTH:
            case InteractiveComponents.SOUTH:
                dispatch(stopGuidePageTurboScroll());
                break;
            default:
        }
    };

export const onMouseOverGuideOperations = () => (dispatch: Function, getState: Function) => {
    const { guideHighlight, inputFactory } = getState();
    const { ghostMode } = guideHighlight;
    const { value } = inputFactory;

    switch (value) {
        case InteractiveComponents.EAST:
        case InteractiveComponents.WEST:
        case InteractiveComponents.NORTH:
        case InteractiveComponents.SOUTH:
            dispatch(setActiveInteractiveComponent(value));
            dispatch(guideSetCaretVisible());
            dispatch(enableGhostMode());
            return;
        default:
    }

    const position = calculateHighlightPosition(value);
    if (!position) {
        dispatch(enableGhostMode());
        return;
    }
    if (ghostMode) dispatch(disableGhostMode());

    dispatch(setVerticalAndHorizontalPositionAction(position.vertical, position.horizontal));
    dispatch(setHorizontalSession());
};

const calculateHighlightPosition = (
    value: any,
): { horizontal: number; vertical: number } | null => {
    if (!value || !value.includes('_')) return null;
    const positions = value.split('_');
    const horizontal = Number(positions[0]);
    const vertical = Number(positions[1]);
    return { horizontal, vertical };
};

export const onMouseOutGuideOperations = () => (dispatch: Function, getState: Function) => {
    const { inputFactory, turboScroll } = getState();
    const { value } = inputFactory;
    const { isGuidePageScrollActive } = turboScroll;

    switch (value) {
        case InteractiveComponents.EAST:
        case InteractiveComponents.WEST:
        case InteractiveComponents.NORTH:
        case InteractiveComponents.SOUTH:
            dispatch(guideSetCaretInvisible());
            if (isGuidePageScrollActive) dispatch(stopGuidePageTurboScroll());
            break;
        default:
    }
};

export const onMouseClickGuideEastInteractiveComponent =
    () => (dispatch: Function, getState: Function) => {
        const { horizontalPageCount } = getState().guideHighlight;

        if (Math.abs(horizontalPageCount) === 1) dispatch(turnOffPastGuideMode());
        dispatch(onRightSetNextPage());
    };

export const onMouseClickGuideWestInteractiveComponent =
    () => (dispatch: Function, getState: Function) => {
        const { horizontalPageCount } = getState().guideHighlight;

        if (horizontalPageCount === 0) dispatch(turnOnPastGuideMode());
        dispatch(onLeftSetPreviousPage());
    };

export const startGuidePageTurboScroll =
    (isUpward: boolean = false) =>
    (dispatch: Function) => {
        Diagnostics.info(TAG, 'PageScroll enabled');
        Log.i(TAG, 'PageScroll enabled');

        dispatch(startGuidePageScrollAction(isUpward));
    };

export const stopGuidePageTurboScroll = () => (dispatch: Function, getState: Function) => {
    const { LIMIT } = getState().gridGuide;

    Diagnostics.info(TAG, 'PageScroll disabled');
    Log.i(TAG, 'PageScroll disabled');

    dispatch(stopGuidePageScrollAction());
    dispatch(loadGridGuideChannels());
    dispatch(setVerticalPositionAction(LIMIT - 1));
};

export const onMouseWheelGuideOperations = (wheelDeltaY: number) => (dispatch: Function) => {
    if (wheelDeltaY < 0) dispatch(loadGridGuidePreviousChannels());
    if (wheelDeltaY > 0) dispatch(loadGridGuideNextChannels());
};
